import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getDisplayPerPage, getPermissions, getRoles, getTenantIsInternal, saveUser, setIsDirty } from "store/userSlice";
import { canManageEmployeeAssignments } from "helpers/employeeAssignmentAuthorization";
import { api } from "helpers/axios";
import { fetchFields, getFixedFields, getFreeFields, getBalancedScorecardFields, getSystemFields, getCalculationRelatedFields, getConditionalFields, getAllFields, getAllCustomNames } from "store/fieldsSlice";
import { errorMessage, scrollToTop } from "helpers/common";
import * as Sentry from "@sentry/react";
import { fetchSavedFilters, getSavedFilters } from "store/savedFiltersSlice";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { fetchBalanceScorecardList, fetchRegularEvaluationLevels, getBalanceScorecardList, getRegularEvaluationLevels } from "store/balanceScorecardSlice";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { fetchInterviewsSettings, getInterviewsSettings } from "store/interviewsSettingsSlice";
import { fetchGroups } from "store/groupsSlice";
import { fetchGlobalPayfactorValues, fetchPayfactors, getPayfactors } from "store/payfactorSlice";
import hasPermissionTo from "helpers/permission";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import { tableResize } from "helpers/tableResize";
import ExportForm from "components/Forms/Export";
import { helpTextIcon } from "helpers/helpText";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { fetchStateEmployeeFields, getStateEmployeeSections, getStateEmployeeSettings } from "store/stateEmployeeSlice";
import EmployeeRow from "components/EmployeeManagement/DB-EmployeeRow";
import Link from "components/ui/Link";
import { periodActivityTranslationFallback } from "helpers/periodActivityTranslations";
import ListPagination from "components/ui/ListPagination";
import { deeplinkSearchValue, deeplinkStaffId } from "helpers/employeeDeeplink";
import { Button, Input, InputAffix, Select } from "components/ui";

export default function Index({ tabsComponent, search, setCounters, activeTab = 'state_employees' }) {
  const dispatch = useDispatch();

  const { translate, editableTranslate } = useTranslation();

  const { helps } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const displayPerPage = useSelector(getDisplayPerPage);

  const isInternalTenant = useSelector(getTenantIsInternal);

  const userRoles = useSelector(getRoles);
  const canManageAssignments = canManageEmployeeAssignments(userRoles, 'db');

  const stateEmployeeSettings = useSelector(getStateEmployeeSettings);

  const fieldCustomNames = useSelector(getAllCustomNames);

  useEffect(() => {
    dispatch(fetchGlobalPayfactorValues());
  }, [dispatch]);

  const [error, setError] = useState(null);

  const [employees, setEmployees] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalRows: 0,
    totalPages: 1,
    changed: false
  });
  const [softwareSettings, setSoftwareSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  const [sortCols, setSortCols] = useState([['last_name', 'asc', false]]); // field.slug, direction, is_lk_col
  const [sortPinCol, setSortPinCol] = useState(null);

  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDirty, setEmployeeDirty] = useState(false);
  const [employeeLkDirty, setEmployeeLkDirty] = useState(false);

  const [viewValue, setViewValue] = useState(null);
  const [appliedFilter, setAppliedFilter] = useState(null);
  const [searchValue, setSearchValue] = useState(search || '');
  const [quickFilters, setQuickFilters] = useState([]);

  const [fixMode, setFixMode] = useState(false);
  const [fixData, setFixData] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);

  const [showExport, setShowExport] = useState(false);
  const [tableFields, setTableFields] = useState([]);

  const regularEvaluationLevels = useSelector(getRegularEvaluationLevels);

  const permissions = useSelector(getPermissions);

  const savedFilters = useSelector(getSavedFilters);
  const balancedScorecardList = useSelector(getBalanceScorecardList);

  const allFields = useSelector(getAllFields);

  const fieldsFixed = useSelector(getFixedFields);
  const fieldsFree = useSelector(getFreeFields);
  const fieldsBs = useSelector(getBalancedScorecardFields);
  const fieldsSystem = useSelector(getSystemFields);
  const fieldsConditional = useSelector(getConditionalFields);
  const fieldsCalculationRelated = useSelector(getCalculationRelatedFields);

  const stateEmployeeSections = useSelector(getStateEmployeeSections);

  const interviews = useSelector(getInterviewsSettings);

  const payFactors = useSelector(getPayfactors);

  const savedFilterOptions = useMemo(() => {
    const savedArr = [];
    const systemArr = [];

    if (savedFilters) {
      savedFilters.forEach(s => {
        if (s?.type === 'system') {
          systemArr.push({
            label: s?.translation_key ? translate(s.translation_key, periodActivityTranslationFallback(s.translation_key, s.name)) : s.name,
            value: s.hash_id,
            is_system: true
          });
        } else {
          savedArr.push({
            label: s?.translation_key ? translate(s.translation_key, periodActivityTranslationFallback(s.translation_key, s.name)) : s.name,
            value: s.hash_id,
            is_system: false
          });
        }
      });
    }

    return [
      {
        label: translate('filters.saved_filters'),
        options: savedArr
      },
      {
        label: translate('filters.system_filters'),
        options: systemArr
      }
    ];
  }, [savedFilters]);

  const flatSavedFilterOptions = useMemo(() => {
    return savedFilterOptions.flatMap((g) => g?.options || []);
  }, [savedFilterOptions]);

  const getActiveAndMapFields = (fields) => {
    if (!fields) return [];
    return fields.filter(f => f.is_active).map(f => ({
      ...f,
      key: f.translation_key.replace('field.', '')
    }));
  };

  const fixedFields = useMemo(() => getActiveAndMapFields(fieldsFixed), [fieldsFixed]);
  const freeFields = useMemo(() => getActiveAndMapFields(fieldsFree), [fieldsFree]);
  const bsFields = useMemo(() => getActiveAndMapFields(fieldsBs), [fieldsBs]);
  const systemFields = useMemo(() => getActiveAndMapFields(fieldsSystem), [fieldsSystem]);
  const conditionalFields = useMemo(() => getActiveAndMapFields(fieldsConditional), [fieldsConditional]);
  const calculationRelatedFields = useMemo(() => getActiveAndMapFields(fieldsCalculationRelated), [fieldsCalculationRelated]);

  const filterFieldsByDisplay = (fields, displayIn) => {
    return fields.filter(f => f.display_in.includes(displayIn));
  };

  const [fixedFieldsView, fixedFieldsData] = useMemo(() => {
    return [
      filterFieldsByDisplay(fixedFields, 'employee_management'),
      filterFieldsByDisplay(fixedFields, 'employee_data')
    ];
  }, [fixedFields]);
  const [freeFieldsView, freeFieldsData] = useMemo(() => {
    return [
      filterFieldsByDisplay(freeFields, 'employee_management'),
      filterFieldsByDisplay(freeFields, 'employee_data')
    ];
  }, [freeFields]);
  const [balancedScorecardFieldsView] = useMemo(() => {
    return [
      filterFieldsByDisplay(bsFields, 'employee_management')
    ];
  }, [bsFields]);
  const [systemFieldsView, systemFieldsData] = useMemo(() => {
    return [
      filterFieldsByDisplay(systemFields, 'employee_management'),
      filterFieldsByDisplay(systemFields, 'employee_data')
    ];
  }, [systemFields]);
  const [conditionalFieldsView, conditionalFieldsData] = useMemo(() => {
    return [
      filterFieldsByDisplay(conditionalFields, 'employee_management'),
      filterFieldsByDisplay(conditionalFields, 'employee_data')
    ];
  }, [conditionalFields]);
  const [calculationRelatedFieldsView, calculationRelatedFieldsData] = useMemo(() => {
    return [
      filterFieldsByDisplay(calculationRelatedFields, 'employee_management'),
      filterFieldsByDisplay(calculationRelatedFields, 'employee_data')
    ];
  }, [calculationRelatedFields]);

  const maxBalancedScorecard = useMemo(() => {
    const configuredLimit = stateEmployeeSettings?.state_employee_max_balanced_scorecard;

    if (configuredLimit && typeof configuredLimit === 'object') {
      const totalPerEmployee = Number.parseInt(configuredLimit?.total_per_employee, 10);
      return Number.isFinite(totalPerEmployee) ? totalPerEmployee : 5;
    }

    const legacyLimit = Number.parseInt(configuredLimit, 10);
    return Number.isFinite(legacyLimit) ? legacyLimit : 5;
  }, [stateEmployeeSettings]);

  const stateEmployeeSectionsWithCustomName = useMemo(() => {
    if (!stateEmployeeSections) return null;

    const newArr = structuredClone(stateEmployeeSections);

    if (!fieldCustomNames) return newArr;

    for (const section of newArr) {
      for (const field of section?.fields) {
        if (field?.field_type === 'compound') {
          for (const component of field?.components) {
            if (component?.type === 'layout') {
              for (const col of component?.columns) {
                for (const component2 of col?.components) {
                  if (component2?.type === 'input' && fieldCustomNames.hasOwnProperty(component2?.translation_key)) {
                    component2.custom_name = fieldCustomNames[component2?.translation_key];
                  }
                };
              }
            } else if (fieldCustomNames.hasOwnProperty(component?.translation_key)) {
              component.custom_name = fieldCustomNames[component?.translation_key];
            }
          }
        } else if (fieldCustomNames.hasOwnProperty(field?.translation_key)) {
          field.custom_name = fieldCustomNames[field?.translation_key];
        }
      }
    }

    return newArr;
  }, [fieldCustomNames, stateEmployeeSections]);

  const tabsRef = useRef(null);
  const floatingSeparatorRef = useRef(null);

  const theadRef = useRef(null);

  const tableRef = useRef(null);

  const fixDivRef = useRef(null);

  useEffect(() => {
    if (!savedFilters) dispatch(fetchSavedFilters());
    dispatch(fetchFields());
  }, []);

  useEffect(() => {
    const theadEl = theadRef.current;
    if (!theadEl) return;

    const tabsEl = tabsRef.current;
    if (!tabsEl) return;

    const floatingEl = floatingSeparatorRef.current;
    if (!floatingEl) return;

    const fixDivEl = fixDivRef.current;

    const parentEl = theadEl.parentElement;
    const tabsParentEl = tabsEl.parentElement;

    const handleScrollTable = () => {
      const coordinates = parentEl.getBoundingClientRect();
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const tabsParentCoordinates = tabsParentEl.getBoundingClientRect();
      if (tabsParentCoordinates.y < navHeight) {
        tabsEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7 - 21}px, 0)`;
        theadEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7 - 21}px, 0)`;
        if (fixDivEl) fixDivEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7 - 21}px, 0)`;

        floatingEl.style.display = 'block';
        floatingEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight - 21}px, 0)`;
        floatingEl.style.height = (tabsEl.offsetHeight + theadEl.offsetHeight + (fixDivEl ? fixDivEl.offsetHeight + 14 : 0) + 21 + 7 + 14) + 'px';
      } else {
        tabsEl.style.transform = 'translate3d(0,0,0)';
        theadEl.style.transform = 'translate3d(0,0,0)';
        if (fixDivEl) fixDivEl.style.transform = 'translate3d(0,0,0)';

        floatingEl.style.display = 'hidden';
        floatingEl.style.transform = 'translate3d(0,0,0)';
        floatingEl.style.height = '.5rem';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, [fixData]);

  useEffect(() => {
    const tf = [...fixedFields, ...calculationRelatedFields, ...conditionalFields, ...systemFields, ...freeFields, ...bsFields];

    let interviewEnabled = false;
    if (stateEmployeeSettings && stateEmployeeSettings.state_employee_interview_enabled) {
      interviewEnabled = true;
    }

    const arr = [];
    for (const f of tf) {
      if (f?.slug === 'personal_interviews' && !interviewEnabled) continue;

      if (f.display_in.includes('employee_management')) {
        f.displayed = f.display_in.includes('employee_management');
        arr.push(f);
      }
    }

    arr.push({
      slug: 'info',
      alignment: 'left',
      name: 'Info',
      translation_key: 'state_employee.info',
      is_balanced_scorecard_specific: false,
      no_sort: true
    });

    setTableFields(arr);
  }, [fixedFields, calculationRelatedFields, conditionalFields, systemFields, freeFields, bsFields, stateEmployeeSettings]);

  const maxColWidth = {
    last_name: 300,
    first_name: 300,
    employee_number: 250,
    email: 300,
    individual_pay_factor: 250,
    individual_average_rating: 300,
    exclude_from_payout: 300,
    balanced_scorecard_template: 400,
    lk: 100,
    info: 50
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    tableResize(table, 'state_employees', true, {}, maxColWidth, setResizedCount);
  }, [employees, triggerColWidth, showExport, showFilter, tableFields]);

  useEffect(() => {
    setEmployeeDirty(false);
    setEmployeeLkDirty(false);

    if (!selectedEmployee) return;

    dispatch(fetchGroups());
    if (!interviews) dispatch(fetchInterviewsSettings());
    if (!payFactors) dispatch(fetchPayfactors());
    if (!balancedScorecardList) dispatch(fetchBalanceScorecardList());
    if (!regularEvaluationLevels?.length) dispatch(fetchRegularEvaluationLevels());
    if (!stateEmployeeSections) dispatch(fetchStateEmployeeFields());
  }, [selectedEmployee]);

  useEffect(() => {
    if (displayPerPage > 0 && pagination.changed) {
      fetchEmployees();

      setPagination({ ...pagination, changed: false });
    }
  }, [pagination.page]);

  useEffect(() => {
    if (displayPerPage > 0) {
      if (searchParams.get('source') === 'calculation') {
        if (fixData) { // wait for fixData if coming from calculation
          fetchEmployees(true);
        }
      } else {
        fetchEmployees(true);
      }
    }
  }, [displayPerPage, sortCols, appliedFilter, fixData, quickFilters, searchParams]);

  useEffect(() => {
    if (searchParams.get('source') === 'calculation') {
      const budgetHashId = searchParams.get('budget_hash_id');
      const budgetName = searchParams.get('budget_name');
      const errorKey = searchParams.get('error_key');

      setFixData({ hash_id: budgetHashId, budget_name: budgetName, error_key: errorKey });
      setFixMode(true);
    } else {
      setFixMode(false);
      setFixData(null);
    }

    setSearchValue(deeplinkSearchValue(searchParams));
  }, [searchParams]);

  useEffect(() => {
    dispatch(setIsDirty(employeeDirty || employeeLkDirty));
  }, [employeeDirty, employeeLkDirty]);

  useEffect(() => {
    if (!viewValue) {
      setAppliedFilter(null);
      return;
    }

    setAppliedFilter(viewValue.value);
  }, [viewValue]);

  useEffect(() => {
    if (!sortPinCol) setTriggerColWidth(p => p + 1);
  }, [sortCols]);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [showFilter, showExport]);

  const fetchSeq = useRef(0); // only the newest fetch may update the list
  const fetchEmployees = async (resetPage = false, loading = true) => {
    if (loading) setLoading(true);
    const seq = ++fetchSeq.current;
    try {
      let page = pagination.page;
      if (resetPage) page = 1;

      let url = `/employees?page=${page}&limit=${displayPerPage}&include=settings`;
      if (fixData) url += `&fix_error_key=${fixData?.error_key}&budget_hash_id=${fixData?.hash_id}`;
      else if (appliedFilter) url += `&saved_filter_hash_id=${appliedFilter}`;

      let searchQuery = searchParams.get('search');
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const staffId = deeplinkStaffId(searchParams);
      if (staffId) url += `&staff_id=${encodeURIComponent(staffId)}`;

      if (sortCols && sortCols.length > 0) {
        url += `&sort=` + sortCols.map(sc => sc[0] + ':' + sc[1]).join(',');
      }

      if (quickFilters.length > 0) {
        url += `&quick_filters=${quickFilters.join(',')}`;
      }

      const { data, status } = await api.get(url);
      if (seq !== fetchSeq.current) return; // a newer fetch superseded this one
      if (status === 200) {
        if (loading) setLoading(false);

        const newData = data.data.map((item) => {
          const information = {};

          item.employeeInformation.data.forEach(info => {
            information[info.key] = info.value;
          });

          return { ...item, information };
        });

        setEmployees(newData);

        const tempPagination = data.meta.pagination;
        setPagination({ totalRows: +tempPagination.total, page, totalPages: tempPagination.total_pages, changed: false });
        setSoftwareSettings(data.meta.settings);
        setCounters(prev => ({ ...prev, [activeTab]: +tempPagination.total }));

        setError(null);
        if (loading) {
          const linkedEmployee = searchParams.get('employee');
          setSelectedEmployee(linkedEmployee && newData.some(employee => employee.hash_id === linkedEmployee)
            ? linkedEmployee
            : null);
        }
      }
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      if (loading) setLoading(false);
      setError(errorMessage(err, 'fetch employees'));
      setCounters(prev => ({ ...prev, [activeTab]: 0 }));
      Sentry.captureException(err);
    }
  };

  const handleSearchFilter = (e) => {
    e?.preventDefault();
    setSearchParams({
      search: searchValue
    });
  };

  const handleAddEmployee = () => {
    if (!employees) return;

    if (selectedEmployee !== true) {
      setSelectedEmployee(true);
      setEmployees([null, ...employees]);
      scrollToTop();
    }
  };

  const handleCancelAddEmployee = () => {
    setSelectedEmployee(null);
    setEmployees(employees.filter(e => e));
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field.slug) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field.slug, 'asc', field.is_balanced_scorecard_specific]);
    }
    setSortCols(newSortCols);
  };

  const getSortCol = (field) => {
    for (const sc of sortCols) {
      if (sc[0] === field.slug) return sc;
    }
    return null;
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field.slug) {
      setSortPinCol(null);
      setSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field.slug);
      setSortCols(sortCols.filter(sc => sc[0] === field.slug));
    }
  };

  const handleFilter = (e) => {
    e?.currentTarget?.blur();
    setShowFilter(!showFilter);
    setShowExport(false);
  };

  const handleCheckQuickFilters = (key, checked) => {
    if (checked) {
      setQuickFilters([...quickFilters, key]);
    } else {
      setQuickFilters(quickFilters.filter(r => r !== key));
    }
  };

  const handleExport = (e) => {
    e?.currentTarget?.blur();
    setShowExport(!showExport);
    setShowFilter(false);
  };

  const getLabel = (key, fallback, customName = null) => {
    const sKey = key.replaceAll('.', '-');
    const help = helps && helps.filter(h => h.key === sKey)[0] || null;
    const abbreviation = key + '.abbreviation';

    // on mouse over, show the full text in a tooltip using ReactTooltip
    return <>
      <span data-tooltip-id={sKey}>{customName || editableTranslate(abbreviation, translate(key, fallback))}</span>
      {help && helpTextIcon(sKey, help.short_text, 'text-normal')}
      {!customName && <ReactTooltip id={sKey} place="bottom" content={editableTranslate(key, fallback)} clickable />}
    </>
  };

  return <>
    <div className="row flex-nowrap">
      <div className={`col-lg ${showExport || showFilter ? 'col-md-9 section-col-right' : ''} min-height-after-navbar`}>
        <div className="bg-white w-100 p-4 content-card h-100">
          <div className="position-relative">
            <div ref={floatingSeparatorRef} className="floating-separator"></div>
          </div>
          <div ref={tabsRef} className="d-flex justify-content-between align-items-center border-bottom employee-management-tabs-div mb-4">
            {tabsComponent}
            <div className="position-relative">
              {
                !fixMode && <div className="d-flex align-items-center">
                  <form className="form-inline" onSubmit={handleSearchFilter}>
                    <InputAffix className={searchValue ? 'bg-red-dark-soft' : ''} prefix={<i className="bi bi-search mr-2"></i>}>
                      <Input data-testid="search-input" type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} autoComplete="false" placeholder={translate('form.search')} maxLength={255} />
                    </InputAffix>
                  </form>

                  {
                    hasPermissionTo('reports.employee_data.*', permissions) && <Button variant="secondary" onClick={handleExport} data-testid="export-button" className="ml-3"><i className="bi bi-file-earmark-arrow-down mr-1"></i> {editableTranslate('global.export', 'Bericht/Export')}</Button>
                  }

                  <div className="position-relative ml-3">
                    <Button variant="secondary" onClick={handleFilter} data-testid="filter-button" className="shadow-none"><i className="bi bi-filter"></i> {editableTranslate('form.filter')}</Button>
                  </div>
                  {
                    hasPermissionTo('employee_management.manage_employees.*', permissions) && canManageAssignments && <Button variant="secondary" className="shadow-none ml-3" onClick={handleAddEmployee} data-testid="add-employee-button">
                      <i className="bi bi-person-plus"></i> {editableTranslate('employee_management.add_employee', 'Hinzufügen')}
                    </Button>
                  }
                </div>
              }
              <div className="employee-filters-separator"></div>
            </div>
          </div>

          {
            error ? <div>
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div> :

              <div className={''}>
                <div className="mt-3">
                  <div className="table-responsive has-custom-scrollbar">
                    <table className="table employees-table" ref={tableRef} data-testid="employees-table">
                      <thead ref={theadRef} className="sticky-top">
                        <tr>
                          {/* <th scope="col">ID</th> */}
                          <th scope="col" className="text-center w-1px"></th>
                          {
                            tableFields.map((field, i) => {
                              const sc = getSortCol(field);

                              return <th key={i} scope="col" className={`${!field?.no_sort ? 'cursor-pointer' : ''} ${field.alignment ? 'text-' + field.alignment : ''}`}>
                                <span className="float-right floating-icons">
                                  {
                                    (!field?.no_sort) && <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${sc && sc[0] === field.slug ? (sc[1] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol(field)} data-testid={`sort-${field.slug}`} />
                                  }
                                  {
                                    (sc && sc[0] === field.slug) && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === field.slug ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol(field)} data-testid={`sort-pin-${field.slug}`} />
                                  }
                                </span>
                                <span data-testid={`sort-label-${field.slug}`} className="th-span" onClick={() => field?.no_sort ? null : handleChangeSortCol(field)}>
                                  {getLabel(field.translation_key, field.name, field?.custom_name)}
                                  {field.name === 'Trend' && <>
                                    <i className="bi bi-info-circle ml-1" data-tooltip-id={`th-trend-tooltip`}></i>
                                    <ReactTooltip id={`th-trend-tooltip`} place="bottom"
                                      content={editableTranslate('tooltips.field.trend.tooltip')} clickable />
                                  </>}
                                  <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                                  <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                                </span>
                                <div className="th-resize-div" data-field={field.slug}></div>
                              </th>
                            })
                          }
                        </tr>
                      </thead>
                      <tbody>
                        {
                          !loading && Array.isArray(employees) && employees.map((employee, i) => {
                            return <EmployeeRow key={i + (!employee ? 'new' : '')} employee={employee} fetchEmployees={fetchEmployees}
                              regularEvaluationLevels={regularEvaluationLevels}
                              tableFields={tableFields}
                              fixedFieldsData={fixedFieldsData}
                              freeFieldsData={freeFieldsData}
                              calculationRelatedFieldsData={calculationRelatedFieldsData}
                              calculationRelatedFields={calculationRelatedFields}
                              systemFieldsData={systemFieldsData}
                              conditionalFieldsData={conditionalFieldsData}
                              stateEmployeeSections={stateEmployeeSectionsWithCustomName}
                              selectedEmployee={selectedEmployee} setSelectedEmployee={setSelectedEmployee}
                              softwareSettings={softwareSettings}
                              handleCancelAddEmployee={handleCancelAddEmployee}
                              isInternalTenant={isInternalTenant}
                              sortCols={sortCols}
                              setEmployeeDirty={setEmployeeDirty}
                              setEmployeeLkDirty={setEmployeeLkDirty}
                              resizedCount={resizedCount}
                              getLabel={getLabel}
                              userRoles={userRoles}
                              maxBalancedScorecard={maxBalancedScorecard}
                              allFields={allFields}
                            />
                          })
                        }

                        {
                          (loading || !employees) && <tr>
                            <td colSpan={40}>
                              <div className="d-flex justify-content-center" data-testid="loading">
                                <div className="spinner-border" role="status">
                                  <span className="sr-only">Wird geladen...</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        }

                        {
                          !loading && Array.isArray(employees) && employees.length === 0 && <tr>
                            <td colSpan={40} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                  <TableScrollbar tableRef={tableRef} data={employees} trigger={resizedCount} />

                  <ListPagination
                    page={pagination.page}
                    perPage={displayPerPage}
                    totalPages={pagination.totalPages}
                    totalRows={pagination.totalRows}
                    setPage={(page) => setPagination({ ...pagination, page, changed: true })}
                    setPerPage={(perPage) => dispatch(saveUser({ display_per_page: perPage }))}
                  />
                </div>
              </div>
          }
        </div>
      </div>

      {
        showExport && <div className="col-lg-auto col-md-3 section-col-left min-height-after-navbar">
          <div className="bg-white w-100 p-4 content-card h-100">
            <div className="side-header">
              <h5 className="m-0 font-weight-bold">{editableTranslate('global.export', 'Bericht/Export')}</h5>
              <span className="cursor-pointer font-weight-bold balanced-scorecard-close" onClick={() => setShowExport(false)} data-testid="close-button">
                <i className="bi bi-x-lg"></i>
              </span>
            </div>
            <ExportForm templateKey="standard_employee_overview" tableFields={tableFields} otherData={{ totalRows: pagination.totalRows, appliedFilter, search: searchParams.get('search'), staffId: deeplinkStaffId(searchParams), quickFilters }} />
          </div>
        </div>
      }

      {
        showFilter && <div className="col-lg-auto col-md-3 section-col-left min-height-after-navbar">
          <div className="bg-white w-100 p-4 content-card h-100">
            <div className="side-header">
              <h5 className="m-0 font-weight-bold">{editableTranslate('form.filter')}</h5>
              <span className="cursor-pointer font-weight-bold balanced-scorecard-close" onClick={() => setShowFilter(false)} data-testid="close-button">
                <i className="bi bi-x-lg"></i>
              </span>
            </div>
            <div className={``}>
              {/* <i className="bi bi-x-lg" onClick={handleResetFilter}></i> */}

              <div>
                <label className="font-weight-bold">{editableTranslate('form.quick_filters')}</label>

                <div className="form-check mb-2 mt-0">
                  <label className="form-check-label">
                    <input className={`form-check-input`} type="checkbox" data-testid="quick-filter-no-balanced-scorecard" checked={quickFilters.includes('no_balanced_scorecard')} onChange={e => handleCheckQuickFilters('no_balanced_scorecard', e.target.checked)} />
                    <span className="ml-1">{editableTranslate('state_employee.no_evaluation')}</span>
                  </label>
                </div>
                <div className="form-check mb-2 mt-0">
                  <label className="form-check-label">
                    <input className={`form-check-input`} type="checkbox" data-testid="quick-filter-open-balanced-scorecard" checked={quickFilters.includes('open_balanced_scorecard')} onChange={e => handleCheckQuickFilters('open_balanced_scorecard', e.target.checked)} />
                    <span className="ml-1">{editableTranslate('state_employee.open_evaluation')}</span>
                  </label>
                </div>
                <div className="form-check mb-2 mt-0">
                  <label className="form-check-label">
                    <input className={`form-check-input`} type="checkbox" data-testid="quick-filter-no-group" checked={quickFilters.includes('no_group_balanced_scorecard')} onChange={e => handleCheckQuickFilters('no_group_balanced_scorecard', e.target.checked)} />
                    <span className="ml-1">{editableTranslate('state_employee.no_group_evaluation', 'Beurteilung ohne Gruppe')}</span>
                  </label>
                </div>
              </div>
              <hr />
              <div>
                <label className="font-weight-bold">{editableTranslate('employee_management.custom_filter')}</label>
                {
                  flatSavedFilterOptions.length > 0 && <>
                    <div style={{ display: 'contents' }} data-testid="custom-filter-select">
                      <Select className="p-0 peb-rs--grouped" placeholder={translate('form.select_placeholder')} isClearable={true} options={savedFilterOptions} value={viewValue} onChange={setViewValue} />
                    </div>
                    {
                      viewValue && <Link data-testid="update-filter-link" to={`/settings/filters?hash_id=${viewValue.value}`} className="red-dark-link mt-1 d-inline-block"><small>{editableTranslate('employee_management.update_this_filter', 'Diesen Filter aktualisieren')}</small></Link>
                    }
                  </>
                }
                {
                  flatSavedFilterOptions.length === 0 && <p className="m-0">{editableTranslate('employee_management.no_views')} (<Link data-testid="filters-link" to="/settings/filters" className="red-dark-link">link</Link>).</p>
                }
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </>
}
